import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import { PatientsListColumnName, SortByFunctionType } from '@/shared/config';
import { getAgeGroup, parseDateAsLocal, trackEvent } from '@/shared/lib';

import { PatientsFilter, PatientsFiltersType } from '@/entities/patient';

import { parseReportTypesParam } from '@/features/studiesDropdownFilter';

import {
  AllPatientsFilterSettingsType,
  SharedByMeFilterSettingsType,
  SharedWithMeFilterSettingsType,
  SortByStateType,
} from '../config/types';
import { getColumnFilterParams, omitHiddenColumnFilters } from '../lib';
import { getSortByOptions } from '../utils/getSortByOptions';
import { getSortByOptionsForSharingBy } from '../utils/getSortByOptionsForSharingBy';
import { getSortByOptionsForSharingWith } from '../utils/getSortByOptionsForSharingWith';

import { usePatientsListStream } from './usePatientsListStream';
import { useSharedByMeStream } from './useSharedByMeStream';
import { useSharedWithMeStream } from './useSharedWithMeStream';

export type UsePatientsSortAndFiltersArgs = {
  organizationID: string;
  isAllPatientList: boolean;
  isSharedWithMeList: boolean;
  isSharedByMeOrSharedByStaffList: boolean;
  hiddenColumns?: PatientsListColumnName[];
};

// TODO: [16|h] For some reason patient list stream is not closing on unmount which it leads to infinite potential to duplicate opened streams
// This is very important to fix this issue because it could lead to serious performance issues
export const usePatientsSortAndFilters = ({
  organizationID,
  isAllPatientList,
  isSharedWithMeList,
  isSharedByMeOrSharedByStaffList,
  hiddenColumns = [],
}: UsePatientsSortAndFiltersArgs) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const visibleFilterParams = omitHiddenColumnFilters(
    searchParams,
    hiddenColumns,
  );

  const [sortByState, setSortByState] = useState<SortByStateType>({
    sortKey: PatientsListColumnName.Default,
    leastOrGreatest: false,
  });

  const searchValue = searchParams.get('search') ?? '';

  const doctorFilters = visibleFilterParams.get('doctorIDs')?.split(',');
  const studyTypesFilters = visibleFilterParams.get('studyTypes')?.split(',');
  const ageGroupFilters = visibleFilterParams.get('ageGroup')?.split(',');

  const ageGroup = getAgeGroup(ageGroupFilters);
  const studyTypes = studyTypesFilters;
  const reportTypes = parseReportTypesParam(
    visibleFilterParams.get('reportTypes'),
  );

  const lastModifiedFromParam = visibleFilterParams.get('lastModifiedFrom');
  const lastModifiedToParam = visibleFilterParams.get('lastModifiedTo');
  const lastModifiedFrom = lastModifiedFromParam
    ? parseDateAsLocal(lastModifiedFromParam)
    : undefined;
  const lastModifiedTo = lastModifiedToParam
    ? parseDateAsLocal(lastModifiedToParam)
    : undefined;

  const hasTranscriptorParam = visibleFilterParams.get('hasTranscriptor');
  const hasTranscriptor = hasTranscriptorParam
    ? hasTranscriptorParam === 'true'
    : undefined;

  const { openPatientListStream, closePatientListStream } =
    usePatientsListStream({ organizationID });

  const { openSharedWithMeStream, closeSharedWithMeStream } =
    useSharedWithMeStream({ organizationID, searchValue });

  const { openSharedByMeStream, closeSharedByMeStream } = useSharedByMeStream({
    organizationID,
    searchValue,
  });

  const fetchMorePatients = (id: string) => {
    if (!organizationID || !id) {
      return;
    }

    if (isAllPatientList) {
      closePatientListStream(false);

      openPatientListStream({
        id,
        doctorFilters,
        searchString: searchValue,
        sortBy: getSortByOptions(
          sortByState?.sortKey,
          sortByState?.leastOrGreatest,
        ),
        studyTypes,
        reportTypes,
        ageGroup,
        lastModifiedFrom,
        lastModifiedTo,
        hasTranscriptor,
      });
    }

    if (isSharedWithMeList) {
      closeSharedWithMeStream(false);

      openSharedWithMeStream({
        searchString: searchValue,
        id,
        sortBy: getSortByOptionsForSharingWith(
          sortByState?.sortKey,
          sortByState?.leastOrGreatest,
        ),
      });
    }

    if (isSharedByMeOrSharedByStaffList) {
      closeSharedByMeStream(false);

      openSharedByMeStream({
        searchString: searchValue,
        id,
        sortBy: getSortByOptionsForSharingBy(
          sortByState?.sortKey,
          sortByState?.leastOrGreatest,
        ),
      });
    }
  };

  const sortByForAllPatients: SortByFunctionType = (
    sortKey,
    leastOrGreatest,
  ) => {
    if (!organizationID) {
      return;
    }

    const filterSettings: AllPatientsFilterSettingsType = {
      searchString: searchValue,
      id: '',
      doctorFilters,
      sortBy: getSortByOptions(sortKey, leastOrGreatest),
      studyTypes,
      reportTypes,
      ageGroup,
      lastModifiedFrom,
      lastModifiedTo,
      hasTranscriptor,
    };

    setSortByState({ sortKey, leastOrGreatest });

    if (isAllPatientList) {
      closePatientListStream(false);

      openPatientListStream(filterSettings);
    }
  };

  const sortByForSharedWithMe: SortByFunctionType = (
    sortKey,
    leastOrGreatest,
  ) => {
    if (!organizationID) {
      return;
    }

    const filterSettings: SharedWithMeFilterSettingsType = {
      searchString: searchValue,
      id: '',
      sortBy: getSortByOptionsForSharingWith(sortKey, leastOrGreatest),
    };

    setSortByState({ sortKey, leastOrGreatest });

    if (isSharedWithMeList) {
      closeSharedWithMeStream();

      openSharedWithMeStream(filterSettings);
    }
  };

  const sortByForSharedByMe: SortByFunctionType = (
    sortKey,
    leastOrGreatest,
  ) => {
    if (!organizationID) {
      return;
    }

    const filterSettings: SharedByMeFilterSettingsType = {
      searchString: searchValue,
      id: '',
      sortBy: getSortByOptionsForSharingBy(sortKey, leastOrGreatest),
    };

    setSortByState({ sortKey, leastOrGreatest });

    if (isSharedByMeOrSharedByStaffList) {
      closeSharedByMeStream();

      openSharedByMeStream(filterSettings);
    }
  };

  const searchPatients = (searchString: string) => {
    if (!organizationID && !isSharedWithMeList) {
      return;
    }

    if (isAllPatientList) {
      closePatientListStream();

      openPatientListStream({
        searchString,
        id: '',
        doctorFilters,
        studyTypes,
        reportTypes,
        ageGroup,
        lastModifiedFrom,
        lastModifiedTo,
        hasTranscriptor,
      });
    }

    if (isSharedWithMeList) {
      closeSharedWithMeStream();

      openSharedWithMeStream({
        searchString,
        id: '',
        sortBy: getSortByOptionsForSharingWith(
          sortByState?.sortKey,
          sortByState?.leastOrGreatest,
        ),
      });
    }

    if (isSharedByMeOrSharedByStaffList) {
      closeSharedByMeStream();

      openSharedByMeStream({
        searchString,
        id: '',
        sortBy: getSortByOptionsForSharingBy(
          sortByState?.sortKey,
          sortByState?.leastOrGreatest,
        ),
      });
    }
  };

  const filterByStudyTypes = (
    studyTypes: string[],
    hasTranscriptorRecords: boolean | undefined,
    reportTypes: string[],
  ) => {
    closePatientListStream();
    openPatientListStream({
      searchString: searchValue,
      doctorFilters,
      id: '',
      studyTypes,
      reportTypes,
      ageGroup,
      lastModifiedFrom,
      lastModifiedTo,
      hasTranscriptor: hasTranscriptorRecords,
    });
  };

  const filterByAgeGroup = (ageGroup: string[]) => {
    closePatientListStream();
    openPatientListStream({
      searchString: searchValue,
      doctorFilters,
      id: '',
      studyTypes,
      reportTypes,
      ageGroup: getAgeGroup(ageGroup),
      lastModifiedFrom,
      lastModifiedTo,
      hasTranscriptor,
    });
  };

  const filterByDoctors = (doctorsIDs: string[]) => {
    closePatientListStream();

    openPatientListStream({
      searchString: searchValue,
      doctorFilters: doctorsIDs,
      id: '',
      studyTypes,
      reportTypes,
      ageGroup,
      lastModifiedFrom,
      lastModifiedTo,
      hasTranscriptor,
    });
  };

  const filterByLastModified = async (dates: { from?: Date; to?: Date }) => {
    closePatientListStream();

    await new Promise((resolve) => setTimeout(resolve, 100));

    openPatientListStream({
      searchString: searchValue,
      doctorFilters,
      id: '',
      studyTypes,
      reportTypes,
      ageGroup,
      lastModifiedFrom: dates.from,
      lastModifiedTo: dates.to,
      hasTranscriptor,
    });
  };

  // A hidden column takes its filter dropdown with it, so an active filter
  // would keep applying with no way to see or clear it. Drop it instead.
  const clearColumnFilter = (column: PatientsListColumnName) => {
    const params = getColumnFilterParams(column);

    if (!params.some((param) => searchParams.has(param))) {
      return;
    }

    params.forEach((param) => searchParams.delete(param));
    setSearchParams(searchParams);

    switch (column) {
      case PatientsListColumnName.Doctors:
        filterByDoctors([]);
        break;

      case PatientsListColumnName.DateOfBirth:
        filterByAgeGroup([]);
        break;

      case PatientsListColumnName.Studies:
        filterByStudyTypes([], undefined, []);
        break;

      case PatientsListColumnName.LastModified:
        void filterByLastModified({});
        break;
    }
  };

  const onFilterPatientsChange = (patientFilterTab: PatientsFiltersType) => {
    if (!organizationID) {
      return;
    }

    if (patientFilterTab === PatientsFilter.sharedWithMe) {
      trackEvent({
        eventName: 'PatientsPageReceivedReferralsClicked',
      });
    }

    if (
      patientFilterTab === PatientsFilter.sharedByStaff ||
      patientFilterTab === PatientsFilter.sharedByMe
    ) {
      trackEvent({
        eventName: 'PatientsPageSentReferralsClicked',
      });
    }
  };

  // Settings restored for this organization can hide a column whose filter is
  // still in the URL, so the list drops it before the stream opens.
  useEffect(() => {
    if (visibleFilterParams.toString() === searchParams.toString()) {
      return;
    }

    setSearchParams(visibleFilterParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, visibleFilterParams.toString()]);

  const closeStreams = useCallback(() => {
    closePatientListStream();
    closeSharedWithMeStream();
    closeSharedByMeStream();
  }, [closePatientListStream, closeSharedByMeStream, closeSharedWithMeStream]);

  useEffect(() => {
    if (organizationID) {
      closeStreams();

      if (isAllPatientList) {
        openPatientListStream({
          searchString: searchValue,
          id: '',
          doctorFilters,
          studyTypes,
          reportTypes,
          ageGroup,
          lastModifiedFrom,
          lastModifiedTo,
          hasTranscriptor,
        });
      } else if (isSharedWithMeList) {
        openSharedWithMeStream();
      } else if (isSharedByMeOrSharedByStaffList) {
        openSharedByMeStream();
      }
    }

    return () => {
      closeStreams();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    organizationID,
    isAllPatientList,
    isSharedWithMeList,
    isSharedByMeOrSharedByStaffList,
  ]);

  return {
    sortByForSharedWithMe,
    sortByForSharedByMe,
    searchPatients,
    filterByDoctors,
    filterByStudyTypes,
    filterByAgeGroup,
    filterByLastModified,
    clearColumnFilter,
    sortByForAllPatients,
    onFilterPatientsChange,
    fetchMorePatients,
  };
};
